export const DEFAULT_STAFF_FILTERS = {
  search: "",
  status: null,
  page: 1,
  perPage: 25,
  sortField: "created_at",
  sortOrder: "desc",
};

export const LAST_OWNER_GUARD_MESSAGE =
  "Cannot deactivate or demote the last active Platform Owner.";

export const PLATFORM_OWNER_ONLY_MESSAGE =
  "Only a Platform Owner can modify another Platform Owner.";

export const SELF_DEACTIVATE_MESSAGE = "You cannot deactivate your own account.";

export const actorIsPlatformOwner = (user) =>
  (user?.platform_roles || []).some((role) => role.is_platform_owner);

export const staffUserHasPlatformOwnerRole = (staffUser) =>
  (staffUser?.platform_roles || []).some((role) => role.is_platform_owner);

export const buildStaffUsersQuery = (filters, basePath = "super-admin/staff-users") => {
  const params = new URLSearchParams();

  if (filters.search) {
    params.append("search", filters.search);
  }

  if (filters.status === true) {
    params.append("status", "1");
  } else if (filters.status === false) {
    params.append("status", "0");
  }

  params.append("page", String(filters.page || 1));
  params.append("per_page", String(filters.perPage || 25));
  params.append("sort_field", filters.sortField || "created_at");
  params.append("sort_order", filters.sortOrder || "desc");

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const hasActiveStaffFilters = (filters) =>
  Boolean(filters.search || filters.status !== null);

export const canEditStaffUser = (staffUser, currentUser) => {
  if (
    staffUserHasPlatformOwnerRole(staffUser) &&
    !actorIsPlatformOwner(currentUser)
  ) {
    return { allowed: false, reason: PLATFORM_OWNER_ONLY_MESSAGE };
  }

  return { allowed: true, reason: null };
};

export const canDeactivateStaff = (staffUser, currentUserId, currentUser = null) => {
  if (!staffUser) {
    return { allowed: false, reason: "Staff user not found." };
  }

  if (currentUserId && staffUser.id === currentUserId) {
    return { allowed: false, reason: SELF_DEACTIVATE_MESSAGE };
  }

  if (
    currentUser &&
    staffUserHasPlatformOwnerRole(staffUser) &&
    !actorIsPlatformOwner(currentUser)
  ) {
    return { allowed: false, reason: PLATFORM_OWNER_ONLY_MESSAGE };
  }

  if (staffUser.is_last_platform_owner) {
    return { allowed: false, reason: LAST_OWNER_GUARD_MESSAGE };
  }

  return { allowed: true, reason: null };
};

export const wouldRemoveLastOwner = (staffUser, selectedRoleIds, availableRoles) => {
  if (!staffUser?.is_last_platform_owner) {
    return false;
  }

  const ownerRoleIds = (availableRoles || [])
    .filter((role) => role.is_platform_owner)
    .map((role) => role.id);

  if (ownerRoleIds.length === 0) {
    return true;
  }

  return !selectedRoleIds.some((roleId) => ownerRoleIds.includes(roleId));
};

export const collectRolesFromStaff = (staffRows = []) => {
  const roleMap = new Map();

  staffRows.forEach((row) => {
    (row.platform_roles || []).forEach((role) => {
      roleMap.set(role.id, role);
    });
  });

  return Array.from(roleMap.values()).sort((left, right) =>
    left.name.localeCompare(right.name)
  );
};

export const mergeRoleOptions = (primary = [], fallback = []) => {
  const roleMap = new Map();

  [...primary, ...fallback].forEach((role) => {
    if (role?.id) {
      roleMap.set(role.id, role);
    }
  });

  return Array.from(roleMap.values()).sort((left, right) =>
    left.name.localeCompare(right.name)
  );
};
